import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { BrazilDate } from "../../../../utils/BrazilDate";
import {
  metaColetivaService,
  MetaColetiva,
} from "../../../../services/metaColetivaService";
import { lojaService } from "../../../../services/lojaService";
import { Loja } from "../../../../types/loja";
import { MetaColetivaStats } from "../types";

interface RankingMetaColetivaContextData {
  metaColetiva: MetaColetivaStats;
  lojas: Loja[];
  isLoading: boolean;
  selectedLojaId?: number;
  showLojaSelector: boolean;
  selectedLojaNome?: string;
  metasCount: number;
  startDate: string;
  endDate: string;
  appliedStartDate: string;
  appliedEndDate: string;
  toggleLojaSelector: () => void;
  selectAllLojas: () => void;
  selectLoja: (id: number) => void;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  applyFilter: () => void;
}

interface RankingMetaColetivaProviderProps {
  children: React.ReactNode;
}

const RankingMetaColetivaContext =
  createContext<RankingMetaColetivaContextData>(
    {} as RankingMetaColetivaContextData
  );

export const RankingMetaColetivaProvider: React.FC<
  RankingMetaColetivaProviderProps
> = ({ children }) => {
  const todayISO = BrazilDate.todayISO();

  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>();
  const [showLojaSelector, setShowLojaSelector] = useState(false);
  const [startDate, setStartDate] = useState(todayISO);
  const [endDate, setEndDate] = useState(todayISO);
  const [appliedStartDate, setAppliedStartDate] = useState(todayISO);
  const [appliedEndDate, setAppliedEndDate] = useState(todayISO);

  const { data: lojas = [], isLoading: isLoadingLojas } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const { data: metasResponse, isLoading: isLoadingMetas } = useQuery({
    queryKey: [
      "metas-coletivas-ranking",
      appliedStartDate,
      appliedEndDate,
      selectedLojaId,
    ],
    queryFn: () => metaColetivaService.getMetasColetivas({ limit: 1000 }),
    enabled: !!appliedStartDate && !!appliedEndDate,
  });

  const metasData: MetaColetiva[] = useMemo(
    () => metasResponse?.data ?? [],
    [metasResponse?.data]
  );

  const isLoading = isLoadingLojas || isLoadingMetas;

  const metaColetiva: MetaColetivaStats = useMemo(() => {
    if (!metasData.length) {
      return {
        inicializacaoNota: 0,
        producaoNota: 0,
        finalizacaoNota: 0,
        tempo: "00:00:00",
        tempoMetaSistema: "00:00:00",
        metaAtingida: 0,
        erroProducao: 0,
      };
    }

    const firstMeta = metasData[0];
    return {
      inicializacaoNota: firstMeta.inicializacaoNota,
      producaoNota: firstMeta.producaoNota,
      finalizacaoNota: firstMeta.finalizacaoNota,
      tempo: firstMeta.tempoMeta,
      tempoMetaSistema: firstMeta.tempoReal,
      metaAtingida: firstMeta.metaAtingida,
      erroProducao: firstMeta.errosProducao,
    };
  }, [metasData]);

  const filteredMetas = useMemo(() => {
    if (!metasData.length) return [];

    return metasData.filter((meta: MetaColetiva) => {
      if (selectedLojaId && meta.lojaId !== selectedLojaId) {
        return false;
      }

      const metaDate = BrazilDate.formatISO(new Date(meta.createdAt));
      return metaDate >= appliedStartDate && metaDate <= appliedEndDate;
    });
  }, [metasData, selectedLojaId, appliedStartDate, appliedEndDate]);

  const selectedLojaNome = useMemo(
    () => lojas.find((l) => l.id === selectedLojaId)?.nome,
    [lojas, selectedLojaId]
  );

  const toggleLojaSelector = useCallback(() => {
    setShowLojaSelector((prev) => !prev);
  }, []);

  const selectAllLojas = useCallback(() => {
    setSelectedLojaId(undefined);
    setShowLojaSelector(false);
  }, []);

  const selectLoja = useCallback((id: number) => {
    setSelectedLojaId(id);
    setShowLojaSelector(false);
  }, []);

  const applyFilter = useCallback(() => {
    if (startDate && endDate) {
      setAppliedStartDate(startDate);
      setAppliedEndDate(endDate);
    }
  }, [startDate, endDate]);

  return (
    <RankingMetaColetivaContext.Provider
      value={{
        metaColetiva,
        lojas,
        isLoading,
        selectedLojaId,
        showLojaSelector,
        selectedLojaNome,
        metasCount: filteredMetas.length,
        startDate,
        endDate,
        appliedStartDate,
        appliedEndDate,
        toggleLojaSelector,
        selectAllLojas,
        selectLoja,
        setStartDate,
        setEndDate,
        applyFilter,
      }}
    >
      {children}
    </RankingMetaColetivaContext.Provider>
  );
};

export const useRankingMetaColetiva = (): RankingMetaColetivaContextData => {
  const context = useContext(RankingMetaColetivaContext);
  if (!context) {
    throw new Error(
      "useRankingMetaColetiva must be used within RankingMetaColetivaProvider"
    );
  }
  return context;
};
